import { useState, useEffect, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Login from "@/pages/Login";
import DashboardLayout from "@/pages/DashboardLayout";
import Dashboard from "@/pages/Dashboard";
import Orders from "@/pages/Orders";
import OrderDetails from "@/pages/OrderDetails";
import NewOrder from "@/pages/NewOrder";
import Customers from "@/pages/Customers";
import PaymentAccounts from "@/pages/PaymentAccounts";
import Receivers from "@/pages/Receivers";
import Users from "@/pages/Users";
import Imports from "@/pages/Imports";
import Manifests from "@/pages/Manifests";
import Invoices from "@/pages/Invoices";
import Reconciliation from "@/pages/Reconciliation";
import Products from "@/pages/Products";
import PricingZones from "@/pages/PricingZones";
import ReceiverPortal from "@/pages/ReceiverPortal";
import CustomerLayout from "@/pages/CustomerLayout";
import CustomerSignup from "@/pages/CustomerSignup";
import CustomerLogin from "@/pages/CustomerLogin";
import CustomerOrders from "@/pages/CustomerOrders";
import CustomerNewOrder from "@/pages/CustomerNewOrder";
import CustomerOrderDetails from "@/pages/CustomerOrderDetails";
import { Toaster } from "@/components/ui/sonner";

function AuthCallback() {
  const navigate = useNavigate();
  useEffect(() => {
    const hash = window.location.hash;
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) {
      navigate("/login");
      return;
    }
    const sessionId = match[1];
    (async () => {
      try {
        await api.post("/auth/session", { session_id: sessionId });
        window.location.replace("/dashboard");
      } catch {
        navigate("/login");
      }
    })();
  }, [navigate]);
  return <div className="min-h-screen flex items-center justify-center bg-white"><p className="text-sm text-zinc-500">Completing sign-in…</p></div>;
}

function AppRoutes({ user, setUser }) {
  const location = useLocation();

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  const isAuth = !!user;
  const isCustomer = user?.role === "customer";

  return (
    <Routes>
      {/* Public customer routes */}
      <Route path="/customer/signup" element={isCustomer ? <Navigate to="/customer/new" /> : <CustomerSignup setUser={setUser} />} />
      <Route path="/customer/login" element={isCustomer ? <Navigate to="/customer" /> : <CustomerLogin setUser={setUser} />} />

      {/* Authenticated customer app */}
      <Route
        path="/customer"
        element={isCustomer ? <CustomerLayout user={user} setUser={setUser} /> : <Navigate to="/customer/login" />}
      >
        <Route index element={<CustomerOrders />} />
        <Route path="new" element={<CustomerNewOrder />} />
        <Route path="orders/:id" element={<CustomerOrderDetails />} />
      </Route>

      {/* Staff routes */}
      <Route path="/login" element={isAuth && !isCustomer ? <Navigate to={user.role === "receiver" ? "/portal" : "/dashboard"} /> : (isCustomer ? <Navigate to="/customer" /> : <Login onLogin={setUser} />)} />
      <Route path="/" element={isAuth && !isCustomer ? <DashboardLayout user={user} setUser={setUser} /> : <Navigate to={isCustomer ? "/customer" : "/login"} />}>
        <Route index element={<Navigate to={user?.role === "receiver" ? "/portal" : "/dashboard"} />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="orders" element={<Orders />} />
        <Route path="orders/new" element={<NewOrder />} />
        <Route path="orders/:id" element={<OrderDetails user={user} />} />
        <Route path="customers" element={<Customers />} />
        <Route path="products" element={<Products />} />
        <Route path="pricing-zones" element={<PricingZones />} />
        <Route path="payment-accounts" element={<PaymentAccounts />} />
        <Route path="receivers" element={<Receivers />} />
        <Route path="imports" element={<Imports />} />
        <Route path="manifests" element={<Manifests />} />
        <Route path="invoices" element={<Invoices />} />
        <Route path="reconciliation" element={<Reconciliation />} />
        <Route path="portal" element={<ReceiverPortal user={user} />} />
        <Route path="users" element={<Users />} />
      </Route>
    </Routes>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, let AuthCallback handle it
    if (window.location.hash?.includes("session_id=")) {
      setAuthChecked(true);
      return;
    }
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
    } catch {
      setUser(null);
    } finally {
      setAuthChecked(true);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  if (!authChecked) {
    return <div className="min-h-screen flex items-center justify-center bg-white"><p className="text-sm text-zinc-500">Loading…</p></div>;
  }

  return (
    <div className="App">
      <BrowserRouter>
        <AppRoutes user={user} setUser={setUser} />
      </BrowserRouter>
      <Toaster position="bottom-right" duration={2500} />
    </div>
  );
}

export default App;
